import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Filters from '@pages/Trade/Markets/Instruments/Filters'
import { useCreateWatchlist } from '@pages/Trade/Markets/Instruments/hooks/useCreateWatchlist'
import { useUpdateWatchlist } from '@pages/Trade/Markets/Instruments/hooks/useUpdateWatchlist'
import { FAVORITES, TAG_TO_ASSET_CLASS, TAG_TO_TYPE } from '@pages/Trade/utils/constants'

import { BaseModalType } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { Input } from '@ui/components/Input/Input'
import { Modal } from '@ui/components/Modal'
import { CloseIcon } from '@ui/icons/Close.icon'
import { SearchIcon } from '@ui/icons/Search.icon'
import { TableNoResults } from '@ui/v2/Table/Table'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'

import { Box } from '@shared/components/Box'

import { InstrumentSelectRow } from './components/InstrumentSelectRow'
import { InstrumentSkeletonRows } from './components/InstrumentSkeletonRows'

interface AddInstrumentsModalProps {
  accountCode?: string
  ownerExternalId?: string
  favoriteSymbols?: string[]
  watchlistId?: number
  watchlistName: string
}

export const AddInstrumentsModal: BaseModalType<AddInstrumentsModalProps> = ({
  close,
  accountCode,
  ownerExternalId,
  favoriteSymbols = [],
  watchlistId,
  watchlistName,
}) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { mutate: createWatchlist, isPending: isCreating } = useCreateWatchlist()
  const { mutate: updateWatchlist, isPending: isUpdating } = useUpdateWatchlist()
  const isPending = isCreating || isUpdating

  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const showClearIcon = search.length > 0 ? CloseIcon : undefined
  const debouncedSearch = useDebouncedValue(search.trim(), 400)

  const isFavoritesActive = activeTag === FAVORITES

  const filteredFavoriteSymbols = useMemo(() => {
    if (!debouncedSearch) return favoriteSymbols
    const q = debouncedSearch.toLowerCase()
    return favoriteSymbols.filter((s) => s.toLowerCase().includes(q))
  }, [favoriteSymbols, debouncedSearch])

  const queryParams = useMemo(() => {
    if (isFavoritesActive) {
      return filteredFavoriteSymbols.length ? { symbols: filteredFavoriteSymbols.join(',') } : null
    }
    return {
      assetClass: activeTag ? TAG_TO_ASSET_CLASS[activeTag] : undefined,
      types: activeTag && TAG_TO_TYPE[activeTag] ? [TAG_TO_TYPE[activeTag]] : undefined,
      symbols: debouncedSearch ? `*${debouncedSearch}*` : undefined,
      symbolDescription: debouncedSearch || undefined,
    }
  }, [isFavoritesActive, filteredFavoriteSymbols, activeTag, debouncedSearch])

  const { data: instruments = [], isLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.instrumentsModal(
      accountCode,
      activeTag,
      debouncedSearch,
      filteredFavoriteSymbols.join(',')
    ),
    queryFn: () => apiClient.getDxtradeAccountInstruments(accountCode!, queryParams!),
    enabled: !!accountCode && queryParams !== null,
  })

  const handleToggleTag = (tag: string) => {
    setActiveTag(tag === 'All' ? null : tag)
  }

  const toggleInstrument = (symbol: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(symbol)) {
        next.delete(symbol)
      } else {
        next.add(symbol)
      }
      return next
    })
  }

  const handleSave = () => {
    if (watchlistId !== undefined) {
      updateWatchlist(
        { id: watchlistId, payload: { symbols: Array.from(selected) } },
        { onSuccess: close }
      )
    } else if (ownerExternalId) {
      createWatchlist(
        {
          name: watchlistName,
          description: '',
          type: 'PRIVATE',
          kind: 'REGULAR',
          symbols: Array.from(selected),
          owner: { login: ownerExternalId, domain: 'default' },
        },
        { onSuccess: close }
      )
    }
  }

  return (
    <Modal>
      <Modal.Header>{t('Instruments.Add instruments to', { name: watchlistName })}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Input
            label=''
            fullWidth
            placeholder={t('Instruments.Search instruments')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant='search'
            size='lg'
            leftIcon={SearchIcon}
            rightIcon={showClearIcon}
            onEndIconClick={() => setSearch('')}
          />
        </Box>

        <Box fullWidth>
          <Filters
            activeTag={activeTag}
            showSearch={false}
            showManageDropdown={false}
            onToggleTag={handleToggleTag}
          />
        </Box>

        <Box flex direction='column' fullWidth minHeight={390}>
          {isLoading ? (
            <InstrumentSkeletonRows />
          ) : instruments.length === 0 ? (
            <TableNoResults
              title={t('Instruments.No results found')}
              content={
                <Body1 color='text-med-em'>{t('Instruments.Try adjusting your search')}</Body1>
              }
            />
          ) : (
            instruments.map((instrument) => {
              const symbol = instrument.symbol ?? ''
              const selectedSymbol = selected.has(symbol)
              return (
                <InstrumentSelectRow
                  key={symbol}
                  symbol={symbol}
                  symbolDescription={instrument.symbolDescription}
                  selected={selectedSymbol}
                  onToggle={toggleInstrument}
                />
              )
            })
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={handleSave}
          disabled={selected.size === 0 || isPending}
          loading={isPending}
        >
          {t('Instruments.Save')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
